import {createTheme} from "@mui/material";
import {actionButton, brandTokens, chartPalette, fontFamilies} from "./tokens";

// Builds a complete MUI theme for the given mode from the brand tokens.
// Both lightTheme and darkTheme are thin wrappers over this, so the whole
// look of the backoffice is driven from a single place (tokens.js + here).
const buildTheme = (mode) => {
    const brand = brandTokens[mode];
    const isLight = brand.mode === 'light';
    const tabularFigures = {fontFeatureSettings: '"tnum"', fontVariantNumeric: 'tabular-nums'};

    return createTheme({
        palette: {
            mode: brand.mode,
            primary: brand.primary,
            secondary: brand.secondary,
            background: {default: brand.background.default, paper: brand.background.paper},
            text: {primary: brand.text.primary, secondary: brand.text.secondary},
            divider: brand.divider,
            success: {main: brand.success, contrastText: brand.background.paper},
            warning: {main: brand.warning, contrastText: brand.background.paper},
            error: {main: brand.error, contrastText: brand.background.paper},
            info: {main: brand.info, contrastText: brand.background.paper},
        },

        // The chrome surface (app bar, sidebar, login, KPI cards) and the chart
        // palette read from here. New code should prefer `theme.brand.*`.
        brand: {
            chrome: brand.background.chrome,
            onChrome: brand.text.onChrome,
            field: brand.field,
            fonts: fontFamilies,
            chart: chartPalette,
            agingRamp: brand.agingRamp,
            actionButton,
        },

        shape: {borderRadius: 10},

        typography: {
            htmlFontSize: 16,
            fontFamily: fontFamilies.body,
            h1: {fontFamily: fontFamilies.display, fontWeight: 400},
            h2: {fontFamily: fontFamilies.display, fontWeight: 400},
            h3: {fontFamily: fontFamilies.display, fontWeight: 400},
            h4: {fontFamily: fontFamilies.body, fontWeight: 600},
            h5: {fontFamily: fontFamilies.body, fontWeight: 600},
            h6: {fontFamily: fontFamilies.body, fontWeight: 600},
            subtitle1: {fontWeight: 500},
            subtitle2: {fontWeight: 500},
            button: {fontFamily: fontFamilies.body, fontWeight: 600, textTransform: 'none'},
        },

        components: {
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        fontSize: '1rem',
                        backgroundColor: brand.field,
                        color: brand.text.primary,
                        '& .MuiOutlinedInput-notchedOutline': {borderColor: brand.divider},
                        '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: brand.secondary.main},
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: brand.primary.main},
                        // Read-only / disabled fields must stay legible: keep the value in
                        // brand ink (not the washed-out default) and a visible outline.
                        '& .MuiOutlinedInput-input.Mui-disabled': {WebkitTextFillColor: brand.text.primary, opacity: 1},
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {borderColor: brand.divider},
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {backgroundColor: brand.field, color: brand.text.primary},
                    input: {'&.Mui-disabled': {WebkitTextFillColor: brand.text.primary, opacity: 1}},
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: brand.text.secondary,
                        '&.Mui-focused': {color: brand.primary.main},
                        '&.Mui-disabled': {color: brand.text.secondary},
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {color: brand.text.secondary, '&.Mui-focused': {color: brand.primary.main}},
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        backgroundColor: brand.background.paper,
                        color: brand.text.primary,
                        borderColor: brand.divider,
                        '& .MuiDataGrid-cell': {color: brand.text.primary, borderColor: brand.divider, ...tabularFigures},
                        '& .MuiDataGrid-columnHeader': {
                            color: brand.text.onChrome,
                            backgroundColor: brand.background.chrome,
                            borderColor: brand.divider,
                            fontWeight: 700,
                        },
                        '& .MuiDataGrid-row:hover': {backgroundColor: brand.rowHover},
                        '& .MuiDataGrid-footerContainer': {
                            backgroundColor: brand.background.chrome,
                            borderColor: brand.divider,
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {color: brand.text.primary, borderColor: brand.divider, ...tabularFigures},
                    head: {backgroundColor: brand.background.chrome, color: brand.text.onChrome, fontWeight: 700},
                },
            },
            MuiTableContainer: {styleOverrides: {root: {backgroundColor: brand.background.paper}}},
            MuiPaper: {
                styleOverrides: {
                    root: {backgroundColor: brand.background.paper, color: brand.text.primary, backgroundImage: 'none'},
                },
            },
            MuiCard: {
                defaultProps: {elevation: 0},
                styleOverrides: {
                    root: {
                        backgroundColor: brand.background.paper,
                        color: brand.text.primary,
                        border: `0.5px solid ${brand.divider}`,
                        borderRadius: 12,
                        // Soft, warm-tinted depth — layered with the hairline border rather than a hard shadow.
                        boxShadow: isLight
                            ? '0 1px 2px rgba(70,58,48,0.05), 0 2px 8px rgba(70,58,48,0.04)'
                            : '0 1px 2px rgba(0,0,0,0.30), 0 2px 8px rgba(0,0,0,0.22)',
                    },
                },
            },
            MuiTypography: {styleOverrides: {root: {color: brand.text.primary}}},
            MuiLink: {styleOverrides: {root: {color: brand.link}}},
            MuiSvgIcon: {styleOverrides: {root: {color: 'inherit'}}},
            MuiListItemIcon: {styleOverrides: {root: {color: brand.text.secondary, minWidth: 40}}},
            MuiIconButton: {
                styleOverrides: {
                    // Translucent hover so it reads correctly on any surface — including the
                    // brand app bar — instead of painting an opaque disc.
                    root: ({theme}) => ({
                        transition: 'background-color 150ms ease, color 150ms ease',
                        '&:hover': {backgroundColor: theme.palette.action.hover},
                        '&:focus-visible': {outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2},
                    }),
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: '#3A2E25',
                        color: '#F3EADD',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        borderRadius: 8,
                        padding: '6px 10px',
                    },
                    arrow: {color: '#3A2E25'},
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {fontWeight: 500, borderRadius: 8},
                    outlined: {borderColor: brand.divider},
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {borderRadius: 10, fontWeight: 500, alignItems: 'center'},
                    standard: {border: `0.5px solid ${brand.divider}`},
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: brand.appBar.bg,
                        color: brand.appBar.text,
                        backgroundImage: 'none',
                        boxShadow: 'none',
                        borderBottom: `0.5px solid ${brand.divider}`,
                        // Beat MUI's colorPrimary/colorDefault classes so the bar stays
                        // the calm chrome tone regardless of the color prop callers pass.
                        '&.MuiAppBar-colorPrimary': {backgroundColor: brand.appBar.bg, color: brand.appBar.text},
                        '&.MuiAppBar-colorDefault': {backgroundColor: brand.appBar.bg, color: brand.appBar.text},
                        '& .MuiTypography-root': {color: brand.appBar.text},
                    },
                },
            },
            MuiButton: {
                defaultProps: {disableElevation: true},
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        textTransform: 'none',
                        fontWeight: 600,
                        paddingInline: 18,
                        transition: 'background-color 180ms ease, border-color 180ms ease, color 180ms ease, box-shadow 180ms ease',
                        '&:focus-visible': {outline: `2px solid ${brand.primary.main}`, outlineOffset: 2},
                    },
                    outlined: {
                        color: brand.primary.main,
                        borderColor: brand.divider,
                        '&:hover': {borderColor: brand.primary.main, backgroundColor: brand.rowHover},
                    },
                    text: {color: brand.primary.main, '&:hover': {backgroundColor: brand.rowHover}},
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        backgroundColor: brand.field,
                        color: brand.text.primary,
                        '& .MuiOutlinedInput-notchedOutline': {borderColor: brand.divider},
                        '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: brand.secondary.main},
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: brand.primary.main},
                    },
                    icon: {color: brand.text.secondary},
                },
            },
            MuiMenu: {styleOverrides: {paper: {backgroundColor: brand.background.paper, color: brand.text.primary}}},
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        color: brand.text.primary,
                        '&:hover': {backgroundColor: brand.rowHover},
                        '&.Mui-selected': {
                            backgroundColor: brand.background.chrome,
                            '&:hover': {backgroundColor: brand.rowHover},
                        },
                    },
                },
            },
            MuiPopover: {styleOverrides: {paper: {backgroundColor: brand.background.paper, color: brand.text.primary}}},
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {backgroundColor: brand.background.paper, color: brand.text.primary},
                    option: {
                        color: brand.text.primary,
                        '&:hover': {backgroundColor: brand.rowHover},
                        '&[aria-selected="true"]': {backgroundColor: brand.background.chrome},
                    },
                },
            },
        },
    });
};

export default buildTheme;
